/**
 * Language commands for switching the terminal interface language.
 * Dispatches custom events to update the UI language preference.
 *
 * @module terminal/commands/language
 */
import type { CommandResult, CommandContext } from './types';

/**
 * Language switching commands.
 * Allows users to change the terminal interface between English and Norwegian.
 * Changes are applied via CustomEvent dispatch to the window object.
 *
 * @example
 * ```
 * english       - Switch to English
 * norwegian     - Switch to Norwegian (norsk)
 * lang          - Show current language info
 * ```
 *
 * Aliases (via registry):
 * - `en` → `english`
 * - `no` → `norwegian`
 * - `norsk` → `norwegian`
 */
const languageCommands: Record<
	string,
	(args: string[], context: CommandContext) => CommandResult | null
> = {
	english: () => {
		if (typeof window !== 'undefined') {
			window.dispatchEvent(new CustomEvent('terminal-lang', { detail: 'en' }));
		}
		return { type: 'text', lines: ['Language set to English.'] };
	},
	norwegian: () => {
		if (typeof window !== 'undefined') {
			window.dispatchEvent(new CustomEvent('terminal-lang', { detail: 'no' }));
		}
		return { type: 'text', lines: ['Språk satt til norsk.'] };
	},
	lang: () => ({
		type: 'text',
		lines: ['Current language: auto-detected', '', 'Use "english" or "norwegian" to change.']
	})
};

export { languageCommands };
